import { formatInTimeZone } from "date-fns-tz";
import { formatDistanceToNow, formatRelative } from "date-fns";
import type {
  FormatDateOptions,
  DateFormatType,
} from "@multisystemsuite/timezone-engine-shared-types";
import {
  getLocaleMetadata,
  getRegionalPattern,
} from "@multisystemsuite/timezone-engine-world-data";
import { memoize, TimezoneCache } from "@multisystemsuite/timezone-engine-utils";

const formatCache = new TimezoneCache();

const FORMAT_PATTERNS: Record<DateFormatType, string> = {
  ISO: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
  UTC: "yyyy-MM-dd'T'HH:mm:ss'Z'",
  LOCAL: "Pp",
  SHORT: "P",
  LONG: "PPP",
  FULL: "PPPP",
  DATETIME: "Pp",
  TIME_ONLY: "p",
  DATE_ONLY: "P",
};

function toDate(input: Date | string | number): Date {
  if (input instanceof Date) return input;
  return new Date(input);
}

function resolvePattern(options: FormatDateOptions): string {
  const formatType = options.format ?? "ISO";

  if (options.regionalFormat) {
    const regional = getRegionalPattern(options.regionalFormat);
    if (formatType === "TIME_ONLY") return "p";
    if (formatType === "DATE_ONLY" || formatType === "SHORT") return regional;
    return `${regional} HH:mm`;
  }

  return FORMAT_PATTERNS[formatType];
}

function resolveLocale(options: FormatDateOptions): string {
  if (options.locale) return options.locale;
  if (options.regionalFormat) {
    const mapping: Record<string, string> = {
      IN: "en-IN",
      US: "en-US",
      EU: "de-DE",
      UK: "en-GB",
      JP: "ja-JP",
      AR: "ar-AE",
      CN: "zh-CN",
      ISO: "en-GB",
    };
    return mapping[options.regionalFormat] ?? "en-US";
  }
  return "en-US";
}

/** Format date with locale and timezone awareness */
export function formatDate(date: Date | string | number, options: FormatDateOptions = {}): string {
  const d = toDate(date);
  const timezone = options.timezone ?? "UTC";
  const pattern = resolvePattern(options);

  if (options.format === "UTC") {
    return formatInTimeZone(d, "UTC", pattern);
  }

  return formatInTimeZone(d, timezone, pattern);
}

const memoizedFormat = memoize(
  (timestamp: number, timezone: string, format: string, _locale: string) =>
    formatInTimeZone(new Date(timestamp), timezone, format),
  (ts, tz, fmt, loc) => `${ts}:${tz}:${fmt}:${loc}`,
  formatCache,
);

/** Cached date formatting for high-performance scenarios */
export function formatDateCached(
  date: Date | string | number,
  options: FormatDateOptions = {},
): string {
  const d = toDate(date);
  const timezone = options.timezone ?? "UTC";
  const pattern = resolvePattern(options);
  const locale = resolveLocale(options);
  return memoizedFormat(d.getTime(), timezone, pattern, locale);
}

/** Format relative time (e.g. "2 hours ago") */
export function formatRelativeTime(
  date: Date | string | number,
  _baseDate: Date | string | number = new Date(),
): string {
  return formatDistanceToNow(toDate(date), { addSuffix: true });
}

/** Format relative date with timezone context */
export function formatRelativeDate(
  date: Date | string | number,
  baseDate: Date | string | number = new Date(),
): string {
  return formatRelative(toDate(date), toDate(baseDate));
}

/** Format time in 12h or 24h style */
export function formatTime(
  date: Date | string | number,
  timezone: string,
  style: "12h" | "24h" = "24h",
  _locale = "en-US",
): string {
  const pattern = style === "12h" ? "h:mm a" : "HH:mm";
  return formatInTimeZone(toDate(date), timezone, pattern);
}

/** Get locale-aware date format for a locale string */
export function getLocaleDateFormat(locale: string): string {
  const meta = getLocaleMetadata(locale);
  if (meta) return getRegionalPattern(meta.dateFormat);
  return getRegionalPattern("ISO");
}

/** Get locale time format preference */
export function getLocaleTimeFormat(locale: string): "12h" | "24h" {
  const meta = getLocaleMetadata(locale);
  return meta?.timeFormat ?? "24h";
}
